// src/pages/AdminDashboard.jsx — v4c
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Grid, Paper, Typography, Stack, Button, Chip, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Avatar, alpha, Skeleton,
} from "@mui/material";
import RefreshIcon     from "@mui/icons-material/Refresh";
import DownloadIcon    from "@mui/icons-material/Download";
import TrendingUpIcon  from "@mui/icons-material/TrendingUp";
import SchoolIcon      from "@mui/icons-material/School";
import PeopleIcon      from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon      from "@mui/icons-material/Cancel";
import API             from "../api";
import KpiCard         from "../components/KpiCard";
import ApiError        from "../components/ApiError";
import { useApi }      from "../components/useApi";
import { P, pctColor, getPct } from "../theme";
import {
  AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const PIE_COLORS = [P.green, P.red];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: .5 }}>{label}</Typography>
      {payload.map(p => (
        <Typography key={p.name} variant="caption" sx={{ color: p.color, display: "block" }}>
          {p.name} : {p.value}
        </Typography>
      ))}
    </Paper>
  );
}

export default function AdminDashboard() {
  const { loading, error, run, clearError } = useApi();

  const [kpis,   setKpis]   = useState({ exams: 0, users: 0, attempts: 0, passRate: 0, avgPct: 0 });
  const [byDay,  setByDay]  = useState([]);
  const [byExam, setByExam] = useState([]);
  const [pie,    setPie]    = useState([]);
  const [recent, setRecent] = useState([]);
  const [exams,  setExams]  = useState([]);

  // Titre de page
  useEffect(() => { document.title = "Admin — ExamLabs"; }, []);

  const fetchAll = useCallback(async () => {
    await run(async () => {
      const [er, ar, ur] = await Promise.all([
        API.get("/exams/?page_size=100"),
        API.get("/attempts/?page=1&page_size=200"),
        API.get("/users/?page_size=200"),
      ]);
      const exams    = Array.isArray(er.data) ? er.data : (er.data.items || []);
      setExams(exams);
      const attempts = Array.isArray(ar.data) ? ar.data : (ar.data.items || []);
      const users    = Array.isArray(ur.data) ? ur.data : (ur.data.items || []);

      // ── KPIs ──────────────────────────────────────────────────────────────
      const n      = attempts.length;
      const pcts   = attempts.map(getPct);
      const passN  = attempts.filter(a => {
        const threshold = exams.find(e => e.id === a.exam_id)?.pass_threshold ?? 50;
        return getPct(a) >= threshold;
      }).length;
      const avgPct = n ? Math.round(pcts.reduce((s, v) => s + v, 0) / n) : 0;
      setKpis({
        exams:    exams.length,
        users:    users.filter(u => !u.is_admin).length,
        attempts: n,
        passRate: n ? Math.round((passN / n) * 100) : 0,
        avgPct,
      });

      // ── Tentatives par jour ────────────────────────────────────────────────
      const dm = new Map();
      attempts.forEach(a => {
        const k = new Date(a.submitted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
        dm.set(k, (dm.get(k) || 0) + 1);
      });
      setByDay(
        Array.from(dm.entries())
          .map(([day, n]) => ({ day, n }))
          .sort((a, b) => a.day.localeCompare(b.day))
          .slice(-14)
      );

      // ── Score moyen par examen ─────────────────────────────────────────────
      const em = new Map();
      attempts.forEach(a => {
        if (!em.has(a.exam_id)) em.set(a.exam_id, { sum: 0, n: 0 });
        const x = em.get(a.exam_id);
        x.sum += getPct(a); x.n++;
      });
      setByExam(
        exams.filter(e => em.has(e.id)).map(e => {
          const v = em.get(e.id);
          return { exam: e.title.length > 15 ? e.title.slice(0, 15) + "…" : e.title,
                   pct: Math.round(v.sum / v.n) };
        })
      );

      // ── Pie ───────────────────────────────────────────────────────────────
      setPie([
        { name: "Réussis", value: passN },
        { name: "Échoués", value: n - passN },
      ]);

      // ── Récentes avec usernames ───────────────────────────────────────────
      const usersMap = new Map(users.map(u => [u.id, u.username || u.full_name || `#${u.id}`]));
      setRecent(
        [...attempts]
          .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
          .slice(0, 10)
          .map(a => ({
            id:       a.id,
            user:     usersMap.get(a.user_id) || `user#${a.user_id}`,
            exam:     exams.find(e => e.id === a.exam_id)?.title || `Examen #${a.exam_id}`,
            pct:      getPct(a),
            score:    a.score,
            maxScore: a.max_score,
            dt:       a.submitted_at,
          }))
      );
    });
  }, [run]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportCSV = () => {
    const rows = [
      ["ID", "Utilisateur", "Examen", "Score%", "Date"],
      ...recent.map(r => [r.id, r.user, r.exam, r.pct, r.dt]),
    ];
    const csv = rows.map(r =>
      r.map(String).map(v => `"${v.replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "tentatives.csv";
    a.click();
  };

  return (
    <Box>
      {/* ── Header ── */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ sm: "center" }} sx={{ mb: 4 }} spacing={2}>
        <Box>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.1em", color: P.slate[500], mb: .25 }}>
            VUE GLOBALE
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", color: P.slate[800] }}>
            Tableau de bord
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <IconButton onClick={fetchAll} disabled={loading} size="small"
            sx={{ border: `1px solid ${P.slate[300]}`, borderRadius: "8px" }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button startIcon={<DownloadIcon />} onClick={exportCSV} variant="outlined" size="small"
            sx={{ borderColor: P.slate[300], color: P.slate[700], textTransform: "none", borderRadius: "8px" }}>
            Export CSV
          </Button>
          <Button variant="contained" size="small" component={RouterLink} to="/admin/import"
            sx={{ background: P.navy, textTransform: "none", borderRadius: "8px", fontWeight: 700 }}>
            + Import
          </Button>
        </Stack>
      </Stack>

      {/* Erreur réseau */}
      <ApiError message={error} onRetry={() => { clearError(); fetchAll(); }} />

      <Grid container spacing={3}>

        {/* ── KPIs ── */}
        {[
          { icon: <SchoolIcon />,       color: P.navy,  label: "Examens",        value: kpis.exams    },
          { icon: <PeopleIcon />,       color: P.teal,  label: "Étudiants",      value: kpis.users    },
          { icon: <TrendingUpIcon />,   color: P.amber, label: "Tentatives",     value: kpis.attempts },
          { icon: <EmojiEventsIcon />,  color: P.green, label: "Taux réussite",  value: `${kpis.passRate}%` },
        ].map(k => (
          <Grid item xs={6} md={3} key={k.label}>
            <KpiCard {...k} loading={loading} />
          </Grid>
        ))}

        {/* ── Area chart ── */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 300, borderRadius: "12px", border: `1px solid ${P.slate[200]}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, color: P.slate[800] }}>Tentatives par jour</Typography>
              <Chip size="small" label={loading ? "Chargement…" : "À jour"}
                sx={{ fontSize: "0.7rem", background: P.slate[100] }} />
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height="80%" sx={{ borderRadius: 2, mt: 1 }} />
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={byDay}>
                  <defs>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={P.navy} stopOpacity={.25} />
                      <stop offset="95%" stopColor={P.navy} stopOpacity={0}   />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={P.slate[200]} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: P.slate[500] }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: P.slate[500] }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="n" name="Tentatives"
                    stroke={P.navy} fill="url(#areaGrad)" strokeWidth={2.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* ── Pie ── */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 300, borderRadius: "12px", border: `1px solid ${P.slate[200]}` }}>
            <Typography sx={{ fontWeight: 700, color: P.slate[800], mb: 1.5 }}>Réussite / Échec</Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
                <Skeleton variant="circular" width={140} height={140} />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={4}>
                    {pie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "0.78rem" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* ── Bar score par examen ── */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: 280, borderRadius: "12px", border: `1px solid ${P.slate[200]}` }}>
            <Typography sx={{ fontWeight: 700, color: P.slate[800], mb: 1.5 }}>
              Score moyen par examen (%)
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height="80%" sx={{ borderRadius: 2 }} />
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={byExam} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke={P.slate[200]} />
                  <XAxis dataKey="exam" tick={{ fontSize: 11, fill: P.slate[500] }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: P.slate[500] }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="pct" name="Score %" radius={[6, 6, 0, 0]}>
                    {byExam.map((entry, i) => <Cell key={i} fill={pctColor(entry.pct)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* ── Dernières tentatives ── */}
        <Grid item xs={12}>
          <Paper sx={{ borderRadius: "12px", border: `1px solid ${P.slate[200]}`, overflow: "hidden" }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${P.slate[200]}`,
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontWeight: 700, color: P.slate[800] }}>Dernières tentatives</Typography>
              <Button component={RouterLink} to="/admin/results" size="small"
                sx={{ color: P.navy, textTransform: "none", fontWeight: 600 }}>
                Voir tout →
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow sx={{ background: P.slate[50] }}>
                  {["#", "Utilisateur", "Examen", "Score", "Résultat", "Date"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.72rem",
                      textTransform: "uppercase", letterSpacing: "0.05em", color: P.slate[500] }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton height={24} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : recent.map(r => {
                  const passed = r.pct >= (exams.find(e => e.id === r.exam_id)?.pass_threshold ?? 50);
                  const col    = passed ? P.green : P.red;
                  return (
                    <TableRow key={r.id}
                      sx={{ "&:hover": { background: P.slate[50] }, transition: "background .1s" }}>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">#{r.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: "0.72rem", fontWeight: 800,
                            background: alpha(P.navy, .1), color: P.navy }}>
                            {(r.user || "?")[0].toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>{r.user}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{r.exam}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={r.maxScore ? `${r.score}/${r.maxScore}` : r.score ?? "—"}
                          size="small"
                          sx={{ fontWeight: 800, background: alpha(col, .1), color: col }} />
                        <Typography variant="caption" sx={{ display: "block", color: col, fontWeight: 700 }}>
                          {r.pct}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small"
                          icon={passed
                            ? <CheckCircleIcon sx={{ fontSize: "0.8rem !important" }} />
                            : <CancelIcon     sx={{ fontSize: "0.8rem !important" }} />}
                          label={passed ? "Réussi" : "Échoué"}
                          sx={{ fontWeight: 700, background: alpha(col, .1), color: col,
                            "& .MuiChip-icon": { color: col } }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(r.dt).toLocaleDateString("fr-FR", {
                            day: "numeric", month: "short",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && recent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">Aucune tentative enregistrée.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
}